import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';

/**
 * Agentic App Packs — full surface (1:1 port of the approved mockup).
 *
 * Catalog (hero + pack cards + New Pack) → tabbed pack detail (Outcomes & Actions,
 * Agents, Multi-Agent Workflows, App Connectors, Trust & Compliance, Audit,
 * Governance Posture) → provision slideout (review-first / quick-start) → creation
 * wizard. All styling is the approved mockup's, scoped under `.aap-root` (design
 * tokens defined locally on that root so nothing leaks or regresses other pages).
 *
 * Live wiring: the catalog + provisioned state come from GET /agentic-app-packs
 * and /installations; Provision POSTs to /{id}/provision — reusing the existing
 * AgenticAppPackController / PackProvisioningService (no new backend path).
 */
export default function AgenticAppPacksPageV2() {
  const { hasPermission, session } = useAuth();
  const canProvision = !!hasPermission && hasPermission('pack.provision');
  const canAuthor = !!hasPermission && hasPermission('pack.author');
  const isPA = (session?.role || '') === 'Platform Admin';

  // ── live data ──
  const [pack, setPack] = useState(null);        // the Access Review pack (real row)
  const navigate = useNavigate();
  const [provisioned, setProvisioned] = useState(false);
  const [acctProvisioned, setAcctProvisioned] = useState(false);
  // Real ownership posture for the Accountability Registry card (no hardcoded counts):
  //   total agents in the estate + how many have an ACTIVE accountable owner.
  const [acctStats, setAcctStats] = useState({ totalAgents: null, ownershipActive: null });
  const [installation, setInstallation] = useState(null); // provisioned installation (for assignments)
  const [mode, setMode] = useState('quick');     // 'quick' | 'review'

  // ── team access (P9b) — grants on the provisioned pack instance ──
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [grantKind, setGrantKind] = useState('user');   // 'user' | 'group'
  const [grantTarget, setGrantTarget] = useState('');
  const [grantLevel, setGrantLevel] = useState('execute');
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantRemoving, setGrantRemoving] = useState('');

  // ── view / navigation ──
  const [view, setView] = useState('catalog');   // 'catalog' | 'detail'
  const [tab, setTab] = useState('outcomes');

  // ── provision slideout ──
  const [provOpen, setProvOpen] = useState(false);
  const [provStep, setProvStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // (b2) target-tenant selection — admins can provision for another org/tenant
  const [targetMode, setTargetMode] = useState('self'); // 'self' | 'other'
  const [customers, setCustomers] = useState([]);
  const [selCustomer, setSelCustomer] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [tgtOrg, setTgtOrg] = useState('');
  const [tgtTenants, setTgtTenants] = useState([]);
  const [tgtTenant, setTgtTenant] = useState('');

  // ── creation slideout ──
  const [createOpen, setCreateOpen] = useState(false);
  const [cStep, setCStep] = useState(0);
  const [draftKind, setDraftKind] = useState(null); // null | 'draft' | 'published'

  // ── misc ui ──
  const [catPanel, setCatPanel] = useState(false);
  const [addedConns, setAddedConns] = useState([]);
  const [toastMsg, setToastMsg] = useState('');
  const [toastOn, setToastOn] = useState(false);

  const toast = useCallback((m) => {
    setToastMsg(m); setToastOn(true);
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => setToastOn(false), 2200);
  }, []);

  const reload = useCallback(async () => {
    try {
      const [pRes, iRes] = await Promise.all([
        api.getAgenticAppPacks(),
        api.getAgenticAppPackInstallations(),
      ]);
      const packs = Array.isArray(pRes?.data) ? pRes.data : [];
      const arp = packs.find((p) => (p.packKey || '').includes('access-review')) || packs[0] || null;
      setPack(arp);
      const installs = Array.isArray(iRes?.data) ? iRes.data : [];
      const inst = arp ? installs.find((i) => i.packKey === arp.packKey && i.status !== 'archived') : null;
      if (inst) { setProvisioned(true); setInstallation(inst); setMode(inst.mode === 'quick_start' ? 'quick' : 'review'); }
      else { setInstallation(null); }
      const acctInst = installs.find((i) => (i.packKey || '').includes('accountability-registry') && i.status !== 'archived');
      setAcctProvisioned(!!acctInst);
      // Live posture for the Accountability Registry card. Guarded so a stats
      // failure never blanks the pack gallery. apiFetch wraps as { ok, data }.
      try {
        const [agRes, roRes] = await Promise.all([
          api.getAgents({}),
          api.getOwnershipRollup(),
        ]);
        const agents = agRes?.data ?? agRes;
        const rollup = roRes?.data ?? roRes;
        setAcctStats({
          totalAgents: Array.isArray(agents) ? agents.length : null,
          ownershipActive: (rollup && rollup.byState && typeof rollup.byState.ACTIVE === 'number')
            ? rollup.byState.ACTIVE : null,
        });
      } catch (_) { /* leave stats null → card shows a neutral fallback */ }
    } catch (e) { /* catalog stays with static content on error */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ── handlers ──
  const openPack = () => { setView('detail'); setTab('outcomes'); window.scrollTo(0, 0); };
  const showTab = (t) => { setView('detail'); setTab(t); };

  // ── publish (P2) — honest, status-driven ──
  const instStatus = installation?.status || (provisioned ? 'active' : null);
  const [publishing, setPublishing] = useState(false);
  const completePublish = async () => {
    if (!installation?.id) return;
    setPublishing(true);
    try {
      const res = await api.publishPackInstallation(installation.id);
      if (res && res.ok) {
        const r = res.data || {};
        toast(r.status === 'active'
          ? `Published — all ${r.agentsPublished}/${r.agentsTotal} agents live`
          : `${r.agentsPublished}/${r.agentsTotal} agents published — remaining need evals to pass the gate`);
        reload();
      } else { toast((res && res.error) || 'Publish failed — please try again.'); }
    } catch (e) { toast('Publish failed — please try again.'); } finally { setPublishing(false); }
  };

  // ── team access (P9b) ──
  const canAssign = !!hasPermission && hasPermission('assignment.manage');
  const loadGrants = useCallback(async () => {
    if (!installation?.id) { setGrants([]); return; }
    setGrantsLoading(true);
    try {
      const res = await api.getPackAssignments(installation.id);
      setGrants(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setGrants([]); } finally { setGrantsLoading(false); }
  }, [installation]);

  useEffect(() => { if (tab === 'access' && installation?.id) loadGrants(); }, [tab, installation, loadGrants]);

  const addGrant = async () => {
    if (!installation?.id) return;
    if (!grantTarget.trim()) { toast('Enter the user or group ID to grant.'); return; }
    setGrantBusy(true);
    try {
      const body = { accessLevel: grantLevel };
      if (grantKind === 'user') body.userId = grantTarget.trim(); else body.groupId = grantTarget.trim();
      const res = await api.assignPackAccess(installation.id, body);
      if (res && res.ok) {
        const r = res.data || {};
        toast(`Granted across ${r.agentsAssigned ?? 0} agent(s) + ${r.workflowsAssigned ?? 0} workflow(s)`);
        setGrantTarget('');
        await loadGrants();
      } else { toast((res && res.error) || 'Grant failed — please try again.'); }
    } catch (e) { toast('Grant failed — please try again.'); } finally { setGrantBusy(false); }
  };

  const removeGrant = async (g) => {
    if (!installation?.id) return;
    const key = g.userId || g.groupId;
    setGrantRemoving(key);
    try {
      const res = await api.removePackAssignment(installation.id, { userId: g.userId, groupId: g.groupId });
      if (res && res.ok) { toast('Access revoked across the pack'); await loadGrants(); }
      else { toast((res && res.error) || 'Revoke failed — please try again.'); }
    } catch (e) { toast('Revoke failed — please try again.'); } finally { setGrantRemoving(''); }
  };

  const openInstall = () => {
    setProvStep(0); setMode('quick');
    setTargetMode('self');
    // Default the target picker to the caller's pinned scope (customer / org / tenant).
    setSelCustomer(session?.customerId || '');
    setTgtOrg(session?.orgId || '');
    setTgtTenant(session?.tenantId || '');
    setTgtTenants([]);
    setProvOpen(true);
    api.listCustomers().then((r) => setCustomers(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    api.getOrganizations().then((r) => setOrgs(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    if (session?.orgId) {
      api.getTenantsByOrg(session.orgId).then((r) => setTgtTenants(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
    }
  };
  const onCustomerChange = (cid) => { setSelCustomer(cid); setTgtOrg(''); setTgtTenant(''); setTgtTenants([]); };
  const onTgtOrgChange = (orgId) => {
    setTgtOrg(orgId); setTgtTenant('');
    if (orgId) api.getTenantsByOrg(orgId).then((r) => setTgtTenants(Array.isArray(r?.data) ? r.data : [])).catch(() => setTgtTenants([]));
    else setTgtTenants([]);
  };
  const closeInstall = () => { if (!submitting) setProvOpen(false); };

  const wizNext = async () => {
    if (provStep < 2) { setProvStep(provStep + 1); return; }
    if (provStep === 2) {
      if (!pack) { toast('Pack not loaded yet — try again.'); return; }
      if (targetMode === 'other' && !tgtTenant) { toast('Select a target org and tenant to provision into.'); return; }
      const useTarget = targetMode === 'other' && !!tgtTenant;
      setSubmitting(true);
      try {
        const res = await api.provisionAgenticAppPack(
          pack.id,
          mode === 'quick' ? 'quick_start' : 'review_first',
          useTarget ? tgtOrg : undefined,
          useTarget ? tgtTenant : undefined,
        );
        if (res && res.ok) {
          setProvisioned(true);
          setProvStep(3);
          reload();
        } else {
          toast((res && res.error) || 'Provision failed — please try again.');
        }
      } catch (e) {
        toast('Provision failed — please try again.');
      } finally { setSubmitting(false); }
      return;
    }
    // step 3 (done)
    setProvOpen(false); showTab('outcomes');
  };
  const wizBack = () => { if (provStep > 0) setProvStep(provStep - 1); };

  const cOpen = () => { setCStep(0); setDraftKind(null); setCreateOpen(true); };
  const cClose = () => setCreateOpen(false);
  const C_LAST = 6;
  const cNext = () => { if (cStep < C_LAST) setCStep(cStep + 1); };
  const cPrev = () => { if (cStep > 0) setCStep(cStep - 1); };
  const cSave = () => { setDraftKind((k) => k || 'draft'); toast(draftKind ? 'Draft updated' : 'Draft saved to catalog — resume any time'); };
  const cFinish = () => { setDraftKind('published'); setCStep(C_LAST + 1); toast('Agentic App Pack published to catalog'); };

  const addConn = (name) => { setAddedConns((a) => [...a, name]); toast(`${name} added — configure its credentials`); };

  // ── data (approved mockup content) ──
  const FINDINGS = [
    { chk: true, score: 97, color: 'var(--v2-danger)', tier: 'critical', tl: 'crit', type: 'Terminated but active', sub: 'Leaver retains AP-approver 34d post-exit', who: 'J. Rivera', ws: 'workday:emp_4821', sys: 'SAP', grant: 'role:AP_Approver', dec: 'pending', act: 'both' },
    { chk: true, score: 93, color: 'var(--v2-danger)', tier: 'critical', tl: 'crit', type: 'Segregation-of-duties', sub: 'AP-create + AP-approve on one identity', who: 'M. Osei', ws: 'workday:emp_2290', sys: 'SAP', grant: 'role:AP_Create + AP_Approver', dec: 'deferred', act: 'both' },
    { chk: false, score: 88, color: 'var(--v2-warning-alt)', tier: 'high', tl: 'high', type: 'Orphaned account', sub: 'AD account, no HR identity', who: '— no owner', ws: 'svc_batch_07', sys: 'Active Directory', grant: 'CN=svc_batch_07', dec: 'revoked', act: 'evidence' },
    { chk: false, score: 81, color: 'var(--v2-warning-alt)', tier: 'high', tl: 'high', type: 'Over-privilege', sub: 'Grant beyond Sales role model', who: 'L. Tan', ws: 'workday:emp_5533', sys: 'Salesforce', grant: 'profile:System Administrator', dec: 'pending', act: 'both' },
    { chk: false, score: 64, color: 'var(--v2-info)', tier: 'medium', tl: 'med', type: 'Dormant access', sub: 'No sign-in in 214 days', who: 'K. Bauer', ws: 'okta:00u9f2', sys: 'Okta', grant: 'group:VPN-FullTunnel', dec: 'certified', act: 'evidence' },
  ];
  const AGENTS = [
    ['Access Source-of-Truth', 'reads HR / IdP', 'Establishes who should have access (Workday / IdP)', 'Human-approval'],
    ['Entitlement Collector', 'reads target systems', 'Reads who actually has access (AD / SAP / Okta / SFDC / DB)', 'Human-approval'],
    ['Access Reconciliation', 'compares & risk-ranks', 'Emits findings: orphaned / terminated-active / SoD / dormant / over-privilege', 'Human-approval'],
    ['Certification Reviewer-Assist', 'drafts decisions', 'Recommends certify/revoke, routes exceptions to human owners', 'Human-approval'],
    ['Access Remediation', 'executes revocations', 'Executes approved revocations behind a human approval gate', 'Human-controlled'],
    ['Evidence & Attestation', 'seals every step', 'Seals each action to the hash-chained Audit Trail', 'Autonomous'],
  ];
  const FLOW = ['Access Source-of-Truth', 'Entitlement Collector', 'Access Reconciliation', 'Certification Reviewer-Assist', 'Access Remediation', 'Evidence & Attestation'];
  const CONNECTORS = [
    ['🧑‍💼', 'Workday', 'Recommended · source of truth', 'no', 'Not configured', 'Configure'],
    ['🪟', 'Active Directory', 'Recommended · target system', 'no', 'Not configured', 'Configure'],
    ['📘', 'SAP', 'Recommended · target system', 'no', 'Not configured', 'Configure'],
    ['🔑', 'Okta', 'Recommended · target system', 'yes', 'Configured', 'Edit'],
    ['☁️', 'Salesforce', 'Recommended · target system', 'no', 'Not configured', 'Configure'],
    ['🗄️', 'Database (JDBC)', 'Recommended · target system', 'opt', 'Optional', 'Configure'],
  ];
  const ADD_CATALOG = [
    ['Identity Governance (IGA)', ['SailPoint IdentityNow', 'Saviynt', 'Okta IGA', 'Microsoft Entra ID Governance']],
    ['Directory & IdP', ['Microsoft Entra ID', 'Google Workspace', 'Ping Identity']],
    ['ERP · ITSM · Cloud · Data', ['Oracle ERP', 'ServiceNow', 'AWS IAM', 'Snowflake']],
  ];
  const TRUST = AGENTS.map((a) => [a[0], a[3]]);
  const FRAMEWORKS = [
    ['SOX — ITGC Access', 'Periodic access certification + segregation-of-duties review, with sealed evidence', 'met', 'Evidenced'],
    ['SoD (ISACA)', 'Toxic-combination detection + human-gated revocation', 'met', 'Evidenced'],
    ['ISO 27001 — A.9 Access Control', 'Least-privilege reconciliation across identities and target systems', 'met', 'Evidenced'],
    ['SOC 2 — CC6.1 / CC6.2 / CC6.3', 'Logical access provisioning, review, and removal with attestation', 'met', 'Evidenced'],
    ['NIST 800-53 — AC-2 / AC-6', 'Account management + least privilege, continuously reconciled', 'part', 'Partial · live data'],
  ];
  const AUDIT = [
    ['07-03 09:14:22', 'Access Reconciliation', 'agent', '', 'emitted 248 findings', 'run cce87a29', '0d527218'],
    ['07-03 09:14:19', 'Entitlement Collector', 'agent', '', 'read target entitlements', 'node exec · SAP/AD/Okta', '016de910'],
    ['07-03 09:14:11', 'Access Source-of-Truth', 'agent', '', 'read identity snapshot', 'node exec · Workday', 'd098cc9c'],
    ['07-03 09:12:03', 'k. moore', 'reviewer', 'var(--v2-success)', 'certified finding', 'okta:00u9f2', '85e14d5d'],
    ['07-03 09:10:47', 'Access Remediation', 'agent · human gate', 'var(--v2-danger)', 'executed revocation', 'svc_batch_07', '3b369ff3'],
    ['07-03 08:58:30', 'Evidence & Attestation', 'agent', '', 'sealed attestation', 'campaign Q3-FY26', '3f0a7e21'],
    ['07-02 16:40:12', 'system', 'provisioning', 'var(--v2-brand-steel-2)', 'pack provisioned', 'pack v1.0 · 6 agents', 'a1c47b90'],
  ];
  const GOV = [
    ['Autonomy ceiling', 'Human-approval max (Remediation is Human-controlled)', 'Yes — per agent'],
    ['Scope guardrail', 'Each skill limited to its declared systems / actions', 'Yes'],
    ['Audit guardrail', 'Every governed call logged + sealed', 'Locked on'],
    ['Approval routing', 'Revocations → Approval Center → asset owner', 'Yes — routing rules'],
    ['Data lineage', 'Findings carry source + target observation refs', 'Locked on'],
  ];
  const COMING = [
    ['◷', 'Joiner-Mover-Leaver Automation', 'Lifecycle-driven provisioning and de-provisioning with governed approval gates and full attestation.', '5 agents · 1 workflow · —'],
    ['⚑', 'SoD Conflict Monitoring', 'Continuous segregation-of-duties detection with risk-ranked toxic-combination findings.', '4 agents · 1 workflow · —'],
    ['☷', 'Vendor / Third-Party Access', 'Time-boxed external access with automated recertification and evidence sealing.', '5 agents · 1 workflow · —'],
  ];

  const TABS = [
    ['outcomes', 'Outcomes & Actions', null],
    ['agents', 'Agents', '6'],
    ['workflows', 'Multi-Agent Workflows', '1'],
    ['connectors', 'App Connectors', '6'],
    ['trust', 'Trust & Compliance', null],
    ['audit', 'Audit', null],
    ['gov', 'Governance Posture', null],
    // Team Access appears once the pack is provisioned (you assign an instance, not the blueprint).
    ...(provisioned ? [['access', 'Team Access', grants.length ? String(grants.length) : null]] : []),
  ];

  // Org list for the target picker. For Platform Admin, narrow to the selected
  // customer's orgs (when the org payload carries customerId); otherwise the
  // caller's own scope already limits the list.
  const orgsHaveCustomer = orgs.some((o) => o.customerId);
  const custOrgs = (isPA && selCustomer && orgsHaveCustomer) ? orgs.filter((o) => o.customerId === selCustomer) : orgs;
  const provReady = provisioned; // banner + gating

  return (
    <div className="aap-root">
      <style>{AAP_CSS}</style>

      {view === 'catalog' && (
        <section>
          <div className="hero">
            <h1>Prove7 Protected Agentic App Pack</h1>
            <p>An Agentic App Pack delivers a protected outcome end-to-end — the agents, the multi-agent
              workflow, the connectors, and the full Prove7 protection plane (identity, trust, policy, audit)
              — configure to fit your unique enterprise requirements and provision into your tenant in one step.</p>
            <div className="chips">
              <span>◈ Protected agents</span><span>⇄ Multi-agent workflow</span><span>🔌 Your connectors</span>
              <span>◎ Trust &amp; compliance</span><span>⛓ Tamper-evident audit</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--v2-sp-3)', marginBottom: 'var(--v2-sp-1)' }}>
            <h1 style={{ fontSize: 'var(--v2-fs-h2)', margin: 0 }}>Available packs</h1>
            <span style={{ flex: 1 }} />
            {canAuthor && <button className="btn primary" onClick={cOpen}>＋ New Agentic App Pack</button>}
          </div>
          <p className="sub">Each pack is a complete, protected solution — provision like a marketplace app.
            Compose your own from agents, workflows, and connectors you already run.</p>

          <div className="grid">
            <div className={'pack' + (provisioned ? ' prov' : '')} onClick={openPack}>
              <div className="icn">▣</div>
              <h3>Access Review &amp; Reconciliation{' '}
                {provisioned
                  ? <span className="pill prov">✓ Provisioned</span>
                  : <span className="pill ok">Available</span>}
              </h3>
              <p>Six protected agents + one multi-agent workflow that reconcile who <i>should</i> vs. who{' '}
                <i>actually</i> has access across your IGA and target systems, then route findings to human
                decision and remediation.</p>
              <div className="meta">
                <span><b>6</b> agents</span><span><b>1</b> workflow</span><span><b>6</b> connectors</span>
                <span><b>Trust + Audit</b></span>
                <span className="cta">{provisioned ? 'Open ›' : 'Provision ›'}</span>
              </div>
            </div>

            {draftKind && (
              <div className="pack" onClick={openPack}>
                <div className="icn">▣</div>
                <h3>Access Review &amp; Reconciliation{' '}
                  {draftKind === 'published' ? <span className="pill ok">Available</span> : <span className="pill new">Draft</span>}</h3>
                <p>Authored from 6 agents + 1 workflow. Tabs auto-generated: Outcomes, Trust, Audit, Protection.</p>
                <div className="meta"><span><b>6</b> agents</span><span><b>1</b> workflow</span><span><b>3</b> capabilities</span><span><b>v1.0.0</b></span></div>
              </div>
            )}

            <div className={'pack' + (acctProvisioned ? ' prov' : '')} onClick={() => navigate('/accountability')}>
              <div className="icn">◉</div>
              <h3>Accountability Registry{' '}
                {acctProvisioned
                  ? <span className="pill prov">✓ Provisioned</span>
                  : <span className="pill ok">Available</span>}</h3>
              <p>Agent Ownership Lifecycle — who owns what, right now, provably. Six ownership-lifecycle
                agents + one workflow that bind every agent to an accountable human: deny-by-default creation
                gate, materiality tiering, independent validation, orphan-to-suspend remediation,
                accountability certificates, and examiner-ready reports.</p>
              <div className="meta">
                <span><b>{acctStats.totalAgents == null ? '—' : acctStats.totalAgents}</b> agents</span><span><b>{acctStats.ownershipActive == null ? '—' : acctStats.ownershipActive}</b> ownership active</span>
                <span><b>Protection + Audit</b></span>
                <span className="cta">Open ›</span>
              </div>
            </div>

            {COMING.map(([icn, name, desc, meta]) => (
              <div className="pack dim" key={name}>
                <div className="icn" style={{ background: 'linear-gradient(135deg,#4a5a7a,#63739a)' }}>{icn}</div>
                <h3>{name} <span className="pill soon">Coming soon</span></h3>
                <p>{desc}</p>
                <div className="meta">{meta.split(' · ').map((m, i) => <span key={i}>{m}</span>)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {view === 'detail' && (
        <section>
          <a className="back" onClick={() => setView('catalog')}>‹ All packs</a>
          <div className="dhead">
            <div className="icn">▣</div>
            <div className="x">
              <h1>{pack?.name || 'Prove7 Access Review & Reconciliation Governed Pack'}</h1>
              <p className="sub" style={{ margin: 0 }}>Enterprise access certification, reconciled end-to-end by
                six protected agents — orchestrated, decided by humans, scored for trust, and sealed to a
                tamper-evident record.</p>
              <div className="tags">
                <span className="pill ok">Available · v{pack?.version || '1.0'}</span>
                <span className="pill new">Full protection plane</span>
                <span className="pill sim">Runs simulated until connectors configured</span>
              </div>
            </div>
            <div className="act">
              {provisioned ? (
                <>
                  <span className="installed-chip">✓ Provisioned · {
                    instStatus === 'active' ? (mode === 'quick' ? 'Published' : 'Agents in Build for review')
                    : instStatus === 'provisioning' ? 'Publish pending'
                    : instStatus === 'failed' ? 'Provisioning failed' : 'Provisioned'
                  }</span>
                  {instStatus === 'provisioning' && canProvision && (
                    <button className="btn primary sm" disabled={publishing} onClick={completePublish}>{publishing ? 'Publishing…' : 'Complete publish'}</button>
                  )}
                  <button className="btn ghost sm" onClick={() => toast('Re-runs the orchestration workflow')}>Run latest</button>
                </>
              ) : (
                canProvision
                  ? <button className="btn primary" onClick={openInstall}>Provision pack</button>
                  : <span className="pill soon">Requires pack.provision</span>
              )}
            </div>
          </div>

          <div className={'provbanner ' + (provReady ? 'live' : 'preview')}>
            {provReady ? (
              <><span className="ic">✓</span><span><b>Provisioned in your tenant</b> — 6 governed agents
                ({instStatus === 'active'
                    ? (mode === 'quick' ? 'published' : 'in Build for review')
                    : 'in Build — publish pending'}), 1 workflow. Outcomes, Trust &amp; Audit
                are live below.</span></>
            ) : (
              <><span className="ic">ⓘ</span><span><b>Preview of the shipped pack.</b> Nothing is created in your
                tenant until you provision. Figures shown are sample data.</span></>
            )}
          </div>

          <div className="tabs">
            {TABS.map(([id, label, n]) => (
              <button key={id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>
                {label}{n && <span className="n">{n}</span>}
              </button>
            ))}
          </div>

          {/* OUTCOMES */}
          {tab === 'outcomes' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--v2-sp-3)', marginBottom: 'var(--v2-sp-1)' }}>
                <h2 style={{ fontSize: 'var(--v2-fs-h2)', margin: 0, color: 'var(--v2-text-primary)', textTransform: 'none', letterSpacing: 0 }}>Access Reconciliation — Outcomes &amp; Actions</h2>
                <span className="pill sim">{provReady ? 'Simulated data · your tenant' : 'Sample · not provisioned'}</span>
              </div>
              <p className="sub">Campaign <b>Q3 FY26 Workforce Access Review</b> · Run <span className="mono">cce87a29</span>{' '}
                · Certify/Revoke invoke the pack's Reviewer-Assist &amp; Remediation agents through Approval Center.</p>
              <div className="summary">
                <div className="card"><div className="k">Findings</div><div className="v">248</div><div className="d">6 target systems</div></div>
                <div className="card"><div className="k">By tier</div><div className="v">31 <span style={{ fontSize: 'var(--v2-fs-xs)', color: 'var(--v2-danger)' }}>crit</span></div>
                  <div className="tiersplit"><span style={{ background: 'var(--v2-danger)', flex: 31 }} /><span style={{ background: 'var(--v2-warning-alt)', flex: 57 }} /><span style={{ background: 'var(--v2-info)', flex: 96 }} /><span style={{ background: 'var(--v2-neutral)', flex: 64 }} /></div></div>
                <div className="card"><div className="k">Decided</div><div className="v">41%</div><div className="bar"><span style={{ width: '41%', background: 'var(--v2-brand-steel)' }} /></div><div className="d">102 of 248</div></div>
                <div className="card"><div className="k">Remediated</div><div className="v">18%</div><div className="bar"><span style={{ width: '18%', background: 'var(--v2-warning)' }} /></div><div className="d">44 · 0 live</div></div>
                <div className="card"><div className="k">Evidence sealed</div><div className="v">100%</div><div className="bar"><span style={{ width: '100%', background: 'var(--v2-success)' }} /></div><div className="d">hash-chained</div></div>
              </div>
              <div className="filters">
                <div className="seg"><button className="on">All types</button><button>Terminated{' '}active</button><button>SoD</button><button>Orphaned</button><button>Dormant</button><button>Over-priv</button></div>
                <select className="sel2"><option>All systems</option></select>
                <select className="sel2"><option>All tiers</option></select>
                <select className="sel2"><option>Any decision</option></select>
              </div>
              <div className="bulkbar"><span>☑ <b>2</b> selected</span>
                <button className="btn cert sm" onClick={() => toast('Certify sent to Approval Center — Reviewer-Assist invoked')}>Certify selected</button>
                <button className="btn rev sm" onClick={() => toast('Revoke queued — Remediation agent invoked behind human gate')}>Revoke selected</button>
                <span style={{ marginLeft: 'auto' }}>Actions route through protected agents + Approval Center</span></div>
              <div className="tablewrap">
                <table>
                  <thead><tr><th style={{ width: 34 }} /><th style={{ width: 104 }}>Risk ▾</th><th>Finding</th><th>Subject</th><th>Target access</th><th>Decision</th><th style={{ width: 150 }}>Action</th></tr></thead>
                  <tbody>
                    {FINDINGS.map((f, i) => (
                      <tr key={i}>
                        <td><input type="checkbox" defaultChecked={f.chk} /></td>
                        <td><div className="risk"><span className="score" style={{ color: f.color }}>{f.score}</span><span className={'tier ' + f.tier}>{f.tl}</span></div></td>
                        <td><div className="type">{f.type}<small>{f.sub}</small></div></td>
                        <td><div className="who">{f.who}<small>{f.ws}</small></div></td>
                        <td><span className="sys">{f.sys}</span><span className="grant mono">{f.grant}</span></td>
                        <td><span className={'dec ' + f.dec}>{f.dec}</span></td>
                        <td><div className="acts">
                          {f.act === 'both' ? (<>
                            <button className="btn rev sm" onClick={() => toast('Revoke queued — Remediation agent (human gate)')}>Revoke</button>
                            <button className="btn cert sm" onClick={() => toast('Certified — sealed to Audit Trail')}>Certify</button>
                          </>) : (<a className="btn ghost sm" onClick={() => toast('Opening sealed record…')}>↗ Evidence</a>)}
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="foot"><span>Showing 5 of 248 · sorted by risk</span><span style={{ flex: 1 }} /><span>‹ Prev &nbsp;1&nbsp;2&nbsp;3…36&nbsp; Next ›</span></div>
              </div>
              <p className="note">The pack's <b>actionable</b> outcomes surface — not a report. Certify/Revoke invoke
                the installed Reviewer-Assist and Remediation agents through the protected Approval Center + Audit
                Trail rails; remediation runs behind a human gate; every action is sealed.</p>
            </div>
          )}

          {/* AGENTS */}
          {tab === 'agents' && (
            <div className="sec">
              <h2>Included agents — 6</h2>
              <table>
                <thead><tr><th>Agent</th><th>Role in the flow</th><th>Autonomy</th><th>State</th></tr></thead>
                <tbody>
                  {AGENTS.map(([name, sub, role, auto]) => (
                    <tr key={name}><td className="agname">{name}<small>{sub}</small></td><td>{role}</td><td>{auto}</td><td><span className="lc">Protected</span></td></tr>
                  ))}
                </tbody>
              </table>
              <p className="note">On provision, each agent is created as a native protected Let. Open any agent to
                inspect its skills, evals, identity (SPIFFE), and autonomy ceiling — and edit them for your environment.</p>
            </div>
          )}

          {/* WORKFLOWS */}
          {tab === 'workflows' && (
            <div>
              <div className="sec">
                <h2>Access Review Reconciliation <span className="spacer" /><span className="pill ok" style={{ textTransform: 'none', letterSpacing: 0 }}>standalone · v1</span></h2>
                <div className="flow">
                  <div className="fnode fterm">START</div><div className="fconn" />
                  {FLOW.map((n, i) => (<React.Fragment key={n}>
                    <div className="fnode"><span className="k">{n === 'Access Remediation' ? 'AGENT · human gate' : 'AGENT'}</span>{n}</div><div className="fconn" />
                  </React.Fragment>))}
                  <div className="fnode fterm">END</div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'center' }}>
                  <button className="btn sm" onClick={() => toast('Opens this workflow on the Canvas')}>Open in Canvas</button>
                  <button className="btn sm" onClick={() => toast('Runs the orchestration workflow')}>Run workflow</button>
                </div>
              </div>
              <p className="note">A pack can ship more than one workflow (e.g. a scheduled recertification run + an
                ad-hoc investigation run). Each provisions as a <span className="mono">kind=standalone</span> canvas
                workflow with the pack's agents pre-bound to their nodes.</p>
            </div>
          )}

          {/* CONNECTORS */}
          {tab === 'connectors' && (
            <div>
              <div className="sec">
                <h2>App connectors <span className="spacer" /><button className="btn sm" onClick={() => setCatPanel(!catPanel)}>{catPanel ? 'Close catalog' : '+ Add connector'}</button></h2>
                <p style={{ margin: '-4px 0 var(--v2-sp-3)', color: 'var(--v2-text-secondary)', fontSize: 'var(--v2-fs-xs)' }}>The pack recommends the connectors
                  below, but your stack is your own — map them to the IGA and target systems you actually run, add more,
                  or remove what you don't need. Credentials are entered in each connector's own secure config; the agents never see them.</p>
                <div className="clist">
                  {CONNECTORS.map(([ic, n, sub, stat, sl, btn]) => (
                    <div className="row" key={n}><div className="ci">{ic}</div><div className="n">{n}<small>{sub}</small></div>
                      <div className="st"><span className={'cstat ' + stat}>{sl}</span><button className="btn sm" onClick={() => toast(`Opens ${n} connector config`)}>{btn}</button></div></div>
                  ))}
                  {addedConns.map((n) => (
                    <div className="row" key={n}><div className="ci">➕</div><div className="n">{n}<small>Added by you</small></div>
                      <div className="st"><span className="cstat no">Not configured</span><button className="btn sm" onClick={() => toast(`Opens ${n} config`)}>Configure</button></div></div>
                  ))}
                </div>
                {catPanel && (
                  <div className="catalog-panel on">
                    <div style={{ fontSize: 'var(--v2-fs-xs)', fontWeight: 600, marginBottom: 'var(--v2-sp-3)' }}>Add a connector for your environment</div>
                    {ADD_CATALOG.map(([g, items]) => (
                      <div className="catgroup" key={g}><div className="gl">{g}</div>
                        <div className="cchips">{items.map((it) => (<span className="cchip" key={it} onClick={() => addConn(it)}><span className="plus">+</span>{it}</span>))}</div></div>
                    ))}
                    <div className="catgroup" style={{ marginBottom: 0 }}><div className="gl">Not listed?</div>
                      <div className="cchips"><span className="cchip custom" onClick={() => addConn('Custom REST connector')}><span className="plus">+</span>Add custom connector (REST / manifest)</span></div></div>
                  </div>
                )}
              </div>
              <p className="note">Connectors are tenant-scoped instances, not baked into the pack. The pack declares{' '}
                <i>capabilities it needs</i> (read identities, read entitlements, revoke grant); you bind those to
                whatever systems you run. This is what flips <span className="mono">provenance.mode</span> from{' '}
                <b>simulated</b> to <b>live</b>.</p>
            </div>
          )}

          {/* TRUST */}
          {tab === 'trust' && (
            provReady ? (
              <div>
                <div className="sec">
                  <h2>Pack trust posture <span className="spacer" /><span className="pill ok" style={{ textTransform: 'none', letterSpacing: 0 }}>Protected · 6/6 agents</span></h2>
                  <div className="gov">
                    <div className="g"><div className="k">Protection coverage</div><div className="v">6 / 6</div><div className="d">agents protected + identified</div></div>
                    <div className="g"><div className="k">Evals passing</div><div className="v">6 / 6</div><div className="d">≥70% min · last run green</div></div>
                    <div className="g"><div className="k">Policy enforcement</div><div className="v">1,284</div><div className="d">protected calls · 37 blocked</div></div>
                    <div className="g"><div className="k">Conformance</div><div className="v unrated">Unrated</div><div className="d">baseline not yet seeded</div></div>
                  </div>
                  <table>
                    <thead><tr><th>Agent</th><th>Eval</th><th>Autonomy ceiling</th><th>Conformance (Crossing)</th><th>Identity</th><th>Trust posture</th></tr></thead>
                    <tbody>
                      {TRUST.map(([name, auto]) => (
                        <tr key={name}><td className="agname">{name}</td><td>71% pass</td><td>{auto}</td><td className="unrated">Unrated · seed pending</td><td>SPIFFE ✓</td><td><span className="ring"><span className="dot" style={{ background: 'var(--v2-success)' }} />Protected</span></td></tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="note">Trust is the platform's <b>real</b> score, not a fabricated default — 0 conformance
                    evaluations reads <b>Unrated</b>, honestly. The pack ships passing functional evals that can seed the
                    conformance baseline ("The Crossing") in one click, at which point Trust Scores populate.</p>
                </div>
                <div className="sec">
                  <h2>Compliance framework mapping</h2>
                  <table>
                    <thead><tr><th>Framework / control</th><th>What this pack evidences</th><th>Status</th></tr></thead>
                    <tbody>
                      {FRAMEWORKS.map(([f, ev, st, sl]) => (
                        <tr key={f}><td className="agname">{f}</td><td>{ev}</td><td><span className={'fw ' + st}>{sl}</span></td></tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="note">Each mapping links to the specific runs, decisions, and sealed audit events that
                    evidence it — one place for the auditor. "Partial" rows complete once live connectors replace simulated data.</p>
                </div>
              </div>
            ) : (
              <div className="empty"><div className="e-ic">◎</div>
                <h3>Trust &amp; compliance populate after provisioning</h3>
                <p>Trust scores, conformance, and framework evidence are computed from real agent runs. Provision the
                  pack to create the agents in your tenant and begin scoring.</p>
                {canProvision && <button className="btn primary" onClick={openInstall}>Provision pack</button>}
              </div>
            )
          )}

          {/* AUDIT */}
          {tab === 'audit' && (
            provReady ? (
              <div>
                <div className="sec">
                  <h2>Audit trail — pack-scoped <span className="spacer" />
                    <button className="btn sm" onClick={() => toast('Recomputing hash-chain… chain intact')}>Verify integrity</button>
                    <button className="btn sm" onClick={() => toast('Exporting audit events to CSV')}>Export CSV</button></h2>
                  <div className="chainbar"><span className="chainbadge">● Chain intact</span><span style={{ color: 'var(--v2-text-muted)', fontSize: 'var(--v2-fs-xs)' }}>Every action by the pack's agents and workflow is hash-chained and tamper-evident — the same Audit Trail as the broader platform, filtered to this pack.</span></div>
                  <div className="filters">
                    <div className="seg"><button className="on">All events</button><button>Agent runs</button><button>Decisions</button><button>Remediation</button><button>Phase changes</button></div>
                    <select className="sel2"><option>All agents</option></select>
                    <select className="sel2"><option>Last 7 days</option></select>
                  </div>
                  <table className="aud">
                    <thead><tr><th style={{ width: 150 }}>When</th><th>Actor</th><th>Action</th><th>Entity</th><th style={{ width: 120 }}>Hash</th></tr></thead>
                    <tbody>
                      {AUDIT.map(([when, actor, sub, dot, action, entity, hash]) => (
                        <tr key={hash}><td>{when}</td><td className="actor"><span className="adot" style={dot ? { background: dot } : undefined} />{actor}<small>{sub}</small></td><td>{action}</td><td>{entity}</td><td className="hash mono">{hash}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="foot"><span>Showing 7 of 1,540 events · pack-scoped</span><span style={{ flex: 1 }} /><span>Open in platform Audit Trail ›</span></div>
                </div>
                <p className="note">This is the platform's tamper-evident record, scoped to the pack — the customer gets
                  full forensic visibility over their protected outcome without leaving the pack. "Verify integrity"
                  recomputes the chain; a break would surface here.</p>
              </div>
            ) : (
              <div className="empty"><div className="e-ic">⛓</div>
                <h3>The audit trail begins at provisioning</h3>
                <p>Every action by the pack's agents and workflow is hash-chained. The first sealed event is the
                  provisioning itself — provision the pack to start the chain.</p>
                {canProvision && <button className="btn primary" onClick={openInstall}>Provision pack</button>}
              </div>
            )
          )}

          {/* GOVERNANCE */}
          {tab === 'gov' && (
            <div>
              <div className="sec">
                <h2>Protection posture (pre-baked, editable)</h2>
                <div className="gov">
                  <div className="g"><div className="k">Eval gate</div><div className="v">≥ 70%</div><div className="d">per-skill, enforced Build→Decide</div></div>
                  <div className="g"><div className="k">Identity</div><div className="v">SPIFFE</div><div className="d">per-agent SVID, per tenant</div></div>
                  <div className="g"><div className="k">Approvals</div><div className="v">Human gate</div><div className="d">on every revocation</div></div>
                  <div className="g"><div className="k">Evidence</div><div className="v">Hash-chain</div><div className="d">tamper-evident Audit Trail</div></div>
                </div>
                <table className="govlist">
                  <thead><tr><th>Control</th><th>Applied by the pack</th><th>Editable</th></tr></thead>
                  <tbody>{GOV.map(([c, a, e]) => (<tr key={c}><td>{c}</td><td>{a}</td><td>{e}</td></tr>))}</tbody>
                </table>
              </div>
              <p className="note">The pack ships a protection baseline so it's compliant on day one; your team can
                tighten (never silently loosen) any editable control. Locked controls are integrity guarantees the pack depends on.</p>
            </div>
          )}

          {tab === 'access' && (
            <div>
              <div className="sec">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--v2-sp-3)' }}>
                  <h2 style={{ margin: 0, textTransform: 'none', letterSpacing: 0 }}>Team access</h2>
                  <span className="pill sim">{grants.length} grant(s)</span>
                </div>
                <p className="sub">Grant a user or group access to this provisioned pack. Each grant fans out across the
                  pack's <b>agents</b> and <b>workflow</b> so the team can run and manage it in your tenant.</p>

                {canAssign && (
                  <div style={{ display: 'flex', gap: 'var(--v2-sp-3)', alignItems: 'flex-end', flexWrap: 'wrap', margin: 'var(--v2-sp-3) 0' }}>
                    <select className="fin" value={grantKind} onChange={(e) => setGrantKind(e.target.value)} style={{ maxWidth: 120 }}>
                      <option value="user">User</option>
                      <option value="group">Group</option>
                    </select>
                    <input className="fin" value={grantTarget} onChange={(e) => setGrantTarget(e.target.value)}
                      placeholder={grantKind === 'user' ? 'User ID (UUID)' : 'Group ID (UUID)'} style={{ flex: 1, minWidth: 220 }} />
                    <select className="fin" value={grantLevel} onChange={(e) => setGrantLevel(e.target.value)} style={{ maxWidth: 140 }}>
                      <option value="execute">Execute</option>
                      <option value="view">View</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button className="btn primary" disabled={grantBusy} onClick={addGrant}>{grantBusy ? 'Granting…' : 'Grant access'}</button>
                  </div>
                )}

                <div className="tablewrap">
                  <table>
                    <thead><tr><th>Principal</th><th>Type</th><th>Access</th><th /></tr></thead>
                    <tbody>
                      {grantsLoading ? (
                        <tr><td colSpan={4} className="mut">Loading grants…</td></tr>
                      ) : grants.length === 0 ? (
                        <tr><td colSpan={4} className="mut">No grants yet. {canAssign ? 'Add a user or group above.' : 'Ask an admin to grant access.'}</td></tr>
                      ) : grants.map((g) => (
                        <tr key={g.id || g.userId || g.groupId}>
                          <td className="mono">{g.userName || g.groupName || g.userId || g.groupId}</td>
                          <td>{g.userId ? 'User' : 'Group'}</td>
                          <td>{g.accessLevel}</td>
                          <td style={{ textAlign: 'right' }}>
                            {canAssign && (
                              <button className="btn sm" disabled={grantRemoving === (g.userId || g.groupId)}
                                onClick={() => removeGrant(g)}>{grantRemoving === (g.userId || g.groupId) ? 'Revoking…' : 'Revoke'}</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="note">Grants apply to the pack <b>instance</b> in this tenant, not the blueprint. Revoking removes
                  the principal from every agent and workflow in the pack. Access is enforced by the platform's standard RBAC at run time.</p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ===== PROVISION SLIDEOUT ===== */}
      <div className={'scrim' + (provOpen ? ' on' : '')} onClick={closeInstall} />
      <aside className={'slideout' + (provOpen ? ' on' : '')}>
        <div className="sohead" style={{ position: 'relative' }}>
          <button className="cl" onClick={closeInstall}>✕</button>
          <h2>Provision pack</h2>
          <div style={{ color: 'var(--v2-text-muted)', fontSize: 'var(--v2-fs-xs)', marginTop: 2 }}>Access Review &amp; Reconciliation · v1.0</div>
          <div className="steps">{[0, 1, 2].map((s) => <div key={s} className={'s' + (s <= Math.min(provStep, 2) ? ' on' : '')} />)}</div>
        </div>
        <div className="sobody">
          {provStep === 0 && (
            <div className="mstep on">
              <label className="fl">Provision into</label>
              <div className="pick">
                <label className={'pk' + (targetMode === 'self' ? ' sel' : '')}>
                  <input type="radio" name="aap-tmode" checked={targetMode === 'self'} onChange={() => setTargetMode('self')} />
                  This tenant (current) <span className="mut">· self-serve</span>
                </label>
                <label className={'pk' + (targetMode === 'other' ? ' sel' : '')}>
                  <input type="radio" name="aap-tmode" checked={targetMode === 'other'} onChange={() => setTargetMode('other')} />
                  Another org / tenant <span className="mut">· admin — provision for a customer</span>
                </label>
              </div>
              {targetMode === 'other' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--v2-sp-2)', margin: 'var(--v2-sp-2) 0 var(--v2-sp-4)' }}>
                  {isPA && (
                    <select className="fin" value={selCustomer} onChange={(e) => onCustomerChange(e.target.value)}>
                      <option value="">Select customer…</option>
                      {customers.map((c) => (
                        <option key={c.id || c.customerId} value={c.id || c.customerId}>{c.name || c.displayName || c.customerName || c.id}</option>
                      ))}
                    </select>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--v2-sp-3)' }}>
                    <select className="fin" style={{ flex: 1 }} value={tgtOrg} onChange={(e) => onTgtOrgChange(e.target.value)} disabled={isPA && !selCustomer}>
                      <option value="">Select org…</option>
                      {custOrgs.map((o) => <option key={o.id} value={o.id}>{o.name || o.displayName || o.id}</option>)}
                    </select>
                    <select className="fin" style={{ flex: 1 }} value={tgtTenant} onChange={(e) => setTgtTenant(e.target.value)} disabled={!tgtOrg}>
                      <option value="">Select tenant…</option>
                      {tgtTenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.displayName || t.id}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <h3>How should the pack provision?</h3>
              <p className="h">The pack ships with protection pre-baked. Choose whether the six agents go live immediately or land in Build for your team to review first.</p>
              <div className={'choice' + (mode === 'quick' ? ' sel' : '')} onClick={() => setMode('quick')}>
                <div className="r" /><div className="t"><b>Quick-start — pre-published, run-ready</b><small>Agents provision already promoted through Build → Decide → Act → Published, workflow bound. Run immediately; the pack's evals, autonomy ceilings, and approval gates are applied as shipped.</small></div>
                <span className="tag" style={{ background: 'var(--v2-success-soft)', color: 'var(--v2-success)' }}>Fastest</span></div>
              <div className={'choice' + (mode === 'review' ? ' sel' : '')} onClick={() => setMode('review')}>
                <div className="r" /><div className="t"><b>Review-first — provision into Build</b><small>Agents provision in the Build phase so your team reviews skills, runs evals, and protects each before publishing. Safer for regulated rollouts; more steps before first run.</small></div>
                <span className="tag" style={{ background: 'var(--v2-info-soft)', color: 'var(--v2-info)' }}>Recommended for regulated</span></div>
            </div>
          )}
          {provStep === 1 && (
            <div className="mstep on">
              <h3>Connectors</h3>
              <p className="h">The pack recommends these. Configure now, or skip and run in simulated mode — you can connect them any time from the pack's App Connectors tab, and add others for your stack.</p>
              <div className="clist">
                {CONNECTORS.map(([ic, n, sub]) => (<div className="row" key={n}><div className="ci">{ic}</div><div className="n">{n}<small>{sub.replace('Recommended · ', '')}</small></div><button className="btn sm">Configure</button></div>))}
              </div>
              <p className="note" style={{ marginTop: 'var(--v2-sp-3)' }}>Credentials are entered in each connector's secure config by you — never handled by the agents.</p>
            </div>
          )}
          {provStep === 2 && (
            <div className="mstep on">
              <h3>Review &amp; provision</h3>
              <p className="h">Here's what will be provisioned into your tenant.</p>
              <div className="summ">
                <div className="l"><span>Provision mode</span><b>{mode === 'quick' ? 'Quick-start (pre-published, run-ready)' : 'Review-first (provision into Build)'}</b></div>
                <div className="l"><span>Agents</span><b>6 protected Lets</b></div>
                <div className="l"><span>Workflow</span><b>Access Review Reconciliation (standalone)</b></div>
                <div className="l"><span>Connectors</span><b>0 configured · runs simulated</b></div>
                <div className="l"><span>Surfaces</span><b>Outcomes · Trust · Audit — wired</b></div>
                <div className="l"><span>Protection</span><b>Evals ≥70% · SPIFFE · human gates</b></div>
              </div>
            </div>
          )}
          {provStep === 3 && (
            <div className="mstep on"><div className="done"><div className="ck">✓</div>
              <h3 style={{ textAlign: 'center' }}>Pack provisioned</h3>
              <p className="h" style={{ textAlign: 'center' }}>Six agents and the orchestration workflow are provisioned.{' '}
                {mode === 'quick' ? 'They’re published and run-ready.' : 'They’re in Build for your team to review, eval, and publish.'}</p></div></div>
          )}
        </div>
        <div className="sofoot" style={{ justifyContent: provStep === 3 ? 'center' : 'flex-end' }}>
          {provStep > 0 && provStep < 3 && <button className="btn ghost" onClick={wizBack}>Back</button>}
          {provStep < 3 && <button className="btn" onClick={closeInstall}>Cancel</button>}
          <button className="btn primary" onClick={wizNext} disabled={submitting}>
            {submitting ? 'Provisioning…' : provStep === 2 ? (mode === 'quick' ? 'Provision & publish' : 'Provision into Build') : provStep === 3 ? 'Go to Outcomes' : 'Continue'}
          </button>
        </div>
      </aside>

      {/* ===== CREATION SLIDEOUT ===== */}
      <div className={'scrim' + (createOpen ? ' on' : '')} onClick={cClose} />
      <aside className={'slideout' + (createOpen ? ' on' : '')} style={{ width: 'calc(var(--v2-slideout-w) + 60px)' }}>
        <div className="sohead" style={{ position: 'relative' }}>
          <button className="cl" onClick={cClose}>✕</button>
          <h2>New Agentic App Pack</h2>
          <div style={{ color: 'var(--v2-text-muted)', fontSize: 'var(--v2-fs-xs)', marginTop: 2 }}>Compose a pack from what you already run — the tabs generate themselves</div>
          <div className="steps">{Array.from({ length: C_LAST + 1 }).map((_, j) => <div key={j} className={'s' + (j <= Math.min(cStep, C_LAST) ? ' on' : '')} />)}</div>
        </div>
        <div className="sobody">
          {cStep === 0 && (<div className="mstep on"><h3>Pack basics</h3><p className="h">Name and describe the outcome this pack delivers.</p>
            <label className="fl">Name</label><input className="fin" defaultValue="Access Review & Reconciliation" />
            <label className="fl">Summary</label><textarea className="fin" rows={2} defaultValue="Reconcile who should vs. who actually has access, then route findings to decision and remediation." />
            <div style={{ display: 'flex', gap: 'var(--v2-sp-3)' }}>
              <div style={{ flex: 1 }}><label className="fl">Category</label><select className="fin"><option>IGA / Access Governance</option><option>Security Operations</option><option>Finance / Controls</option><option>Custom</option></select></div>
              <div style={{ width: 110 }}><label className="fl">Version</label><input className="fin" defaultValue="1.0.0" /></div>
            </div></div>)}
          {cStep === 1 && (<div className="mstep on"><h3>Select agents</h3><p className="h">Pick the protected agents this pack orchestrates. Only agents you can access are listed; their protection travels with them.</p>
            <div className="pick">
              {['Access Source-of-Truth · Governed · Human-approval', 'Entitlement Collector · Governed', 'Access Reconciliation · Governed', 'Certification Reviewer-Assist · Governed', 'Access Remediation · Governed · Human-controlled', 'Evidence & Attestation · Governed · Autonomous'].map((t) => { const [n, ...m] = t.split(' · '); return (<label className="pk sel" key={n}><input type="checkbox" defaultChecked />{n} <span className="mut">· {m.join(' · ')}</span></label>); })}
              <label className="pk"><input type="checkbox" />Anomaly Watch <span className="mut">· Build</span></label>
            </div>
            <p className="note">6 selected. The <b>Agents</b> tab is generated from this selection.</p></div>)}
          {cStep === 2 && (<div className="mstep on"><h3>Select workflow(s)</h3><p className="h">Pick the standalone orchestration workflow(s) that wire these agents. Agents referenced by a workflow are auto-included.</p>
            <div className="pick"><label className="pk sel"><input type="checkbox" defaultChecked />Access Review Reconciliation <span className="mut">· standalone · 8 nodes · references 6 selected agents</span></label>
              <label className="pk"><input type="checkbox" />Ad-hoc Access Investigation <span className="mut">· standalone · 5 nodes</span></label></div>
            <p className="note">The <b>Multi-Agent Workflows</b> tab is generated from this selection (a pack may ship more than one).</p></div>)}
          {cStep === 3 && (<div className="mstep on"><h3>Connector capabilities</h3><p className="h">Auto-detected from the selected agents' connector-skills. These become the pack's declared <i>capabilities</i>; the customer binds them to their own systems at provision time.</p>
            <div className="clist">
              <div className="row"><div className="ci">🧭</div><div className="n">identity.read<small>from Access Source-of-Truth</small></div><div className="st"><span className="cstat opt">capability</span></div></div>
              <div className="row"><div className="ci">🧾</div><div className="n">entitlement.read<small>from Entitlement Collector</small></div><div className="st"><span className="cstat opt">capability</span></div></div>
              <div className="row"><div className="ci">⛔</div><div className="n">grant.revoke<small>from Access Remediation</small></div><div className="st"><span className="cstat opt">capability</span></div></div>
            </div>
            <p className="note">Recommended connectors (Workday, AD, SAP, Okta…) are attached as suggestions. Actual instances live in the platform <b>Connectors</b> section — <b>Configure</b> in the pack triggers the existing connector Install flow; no duplicate connector path.</p></div>)}
          {cStep === 4 && (<div className="mstep on"><h3>Protection baseline</h3><p className="h">Auto-derived from the selected agents' current protection — review, tighten if needed. This generates the <b>Protection Posture</b> tab.</p>
            <div className="summ"><div className="l"><span>Eval gate</span><b>≥ 70% (min of selected)</b></div><div className="l"><span>Autonomy ceilings</span><b>Human-approval (Remediation: Human-controlled)</b></div><div className="l"><span>Identity</span><b>SPIFFE per agent</b></div><div className="l"><span>Locked controls</span><b>Audit · Data lineage</b></div></div>
            <p className="note">Trust &amp; Compliance + Audit tabs need no configuration — they are read projections over the selected agents' runs and the hash-chained audit, generated automatically.</p></div>)}
          {cStep === 5 && (<div className="mstep on"><h3>Outcome surface</h3><p className="h">Bind the outcome this pack surfaces. This generates the <b>Outcomes &amp; Actions</b> tab.</p>
            <label className="fl">Outcome-producing workflow</label><select className="fin"><option>Access Review Reconciliation</option></select>
            <label className="fl">Producing agent / node</label><select className="fin"><option>Access Reconciliation → emit findings</option></select>
            <label className="fl">Finding contract</label><select className="fin"><option>AccessFinding v1 (risk-ranked)</option><option>Generic run output (table)</option></select>
            <label className="fl">Actions</label>
            <div className="clist"><div className="row"><div className="n">Certify<small>invokes Reviewer-Assist · via Approval Center</small></div><div className="st"><span className="cstat yes">on</span></div></div>
              <div className="row"><div className="n">Revoke<small>invokes Remediation · human gate</small></div><div className="st"><span className="cstat yes">on</span></div></div></div></div>)}
          {cStep === 6 && (<div className="mstep on"><h3>Review — these tabs will be generated</h3><p className="h">The pack is a manifest of references (agentIds, workflowIds, capabilities). Nothing is duplicated; each tab is a projection.</p>
            <div className="summ">
              <div className="l"><span>Outcomes &amp; Actions</span><b>from AccessFinding v1 binding</b></div>
              <div className="l"><span>Agents</span><b>6 selected</b></div>
              <div className="l"><span>Multi-Agent Workflows</span><b>1 selected</b></div>
              <div className="l"><span>App Connectors</span><b>3 capabilities → Connectors section</b></div>
              <div className="l"><span>Trust &amp; Compliance</span><b>auto · projection over agent runs</b></div>
              <div className="l"><span>Audit</span><b>auto · pack-scoped hash-chain</b></div>
              <div className="l"><span>Protection Posture</span><b>auto · derived baseline</b></div>
            </div>
            <p className="note">Saved as <b>DB row + JSON manifest</b>. Publish to make it provisionable from the catalog (needs <span className="mono">pack.author</span>).</p></div>)}
          {cStep > C_LAST && (<div className="mstep on"><div className="done"><div className="ck">✓</div><h3 style={{ textAlign: 'center' }}>Pack created</h3>
            <p className="h" style={{ textAlign: 'center' }}>{draftKind === 'published' ? 'Published. It’s now provisionable from the catalog.' : 'Saved as a draft. It now appears in the catalog.'}</p></div></div>)}
        </div>
        <div className="sofoot" style={{ justifyContent: cStep > C_LAST ? 'center' : 'flex-end' }}>
          {cStep > 0 && cStep <= C_LAST && <button className="btn ghost" onClick={cPrev}>Back</button>}
          {cStep <= C_LAST && <button className="btn" onClick={cClose}>Cancel</button>}
          {cStep <= C_LAST && <button className="btn" onClick={cSave}>Save</button>}
          <button className="btn primary" onClick={cStep < C_LAST ? cNext : cStep === C_LAST ? cFinish : () => { cClose(); setView('catalog'); }}>
            {cStep < C_LAST ? 'Continue' : cStep === C_LAST ? 'Publish to catalog' : 'View in catalog'}
          </button>
        </div>
      </aside>

      <div className={'toast' + (toastOn ? ' on' : '')}>{toastMsg}</div>
    </div>
  );
}

/* Approved-mockup stylesheet, scoped under .aap-root so tokens + component rules
   apply ONLY to this page (zero regression to other surfaces). */
const AAP_CSS = `
.aap-root{
  --v2-brand-navy:#0a1d44; --v2-brand-navy-2:#0e3552;
  --v2-brand-steel:#6f86b8; --v2-brand-steel-2:#8ea0c8; --v2-brand-lt:#aebcda;
  --v2-bg-app:#f4f6fa; --v2-bg-surface:#ffffff; --v2-bg-muted:#f0f3f8;
  --v2-border-default:#e2e8f0; --v2-border-subtle:#eef1f6;
  --v2-text-primary:#0c1424; --v2-text-secondary:#41496a; --v2-text-muted:#7e8db0;
  --v2-action:#0a1d44; --v2-action-hover:#0e3552;
  --v2-danger:#c0392b; --v2-danger-soft:#fdecea;
  --v2-warning:#b7791f; --v2-warning-soft:#fbf3e2; --v2-warning-alt:#d97706; --v2-warning-alt-soft:#fdf2e2;
  --v2-info:#2563eb; --v2-info-soft:#e9f0fe;
  --v2-success:#2ea078; --v2-success-soft:#e8f6f0;
  --v2-neutral:#6b7280; --v2-neutral-soft:#f1f2f4;
  --v2-radius-sm:6px; --v2-radius-md:9px; --v2-radius-lg:12px; --v2-radius-xl:16px; --v2-radius-pill:20px;
  --v2-sp-1:4px; --v2-sp-2:8px; --v2-sp-3:12px; --v2-sp-4:16px; --v2-sp-5:22px; --v2-sp-6:30px;
  --v2-slideout-w:480px;
  --v2-fs-h1:22px; --v2-fs-h2:16px; --v2-fs-body:14px; --v2-fs-sm:13px; --v2-fs-xs:12px; --v2-fs-2xs:11px;
  --v2-shadow:0 4px 18px rgba(14,53,82,.07); --v2-shadow-lg:0 20px 60px rgba(10,29,68,.3);
  color:var(--v2-text-primary); font-size:var(--v2-fs-body); line-height:1.5;
  /* Match the platform's standard page frame (peers use p-4 sm:p-7 max-w-[1500px] mx-auto). */
  min-height:100%; padding:28px; max-width:1500px; margin:0 auto;
}
.aap-root .mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
.aap-root a{color:var(--v2-brand-steel);text-decoration:none;cursor:pointer}
.aap-root button{font-family:inherit}
.aap-root h1{font-size:var(--v2-fs-h1);margin:0 0 var(--v2-sp-1);font-weight:700;letter-spacing:-.2px}
.aap-root .sub{color:var(--v2-text-muted);font-size:var(--v2-fs-sm);margin:0 0 var(--v2-sp-5)}
.aap-root .pill{font-size:var(--v2-fs-2xs);font-weight:600;padding:3px var(--v2-sp-2);border-radius:var(--v2-radius-pill);display:inline-block}
.aap-root .pill.sim{background:var(--v2-warning-soft);color:var(--v2-warning)}
.aap-root .pill.ok{background:var(--v2-success-soft);color:var(--v2-success)}
.aap-root .pill.soon{background:var(--v2-neutral-soft);color:var(--v2-neutral)}
.aap-root .pill.new{background:var(--v2-info-soft);color:var(--v2-info)}
.aap-root .pill.prov{background:var(--v2-brand-navy);color:#fff}
.aap-root .btn{border:1px solid var(--v2-border-default);background:var(--v2-bg-surface);color:var(--v2-text-primary);border-radius:var(--v2-radius-sm);padding:var(--v2-sp-2) var(--v2-sp-4);font-size:var(--v2-fs-sm);font-weight:600;cursor:pointer}
.aap-root .btn:hover{border-color:var(--v2-brand-steel-2)}
.aap-root .btn.primary{background:var(--v2-action);color:#fff;border:none}.aap-root .btn.primary:hover{background:var(--v2-action-hover)}
.aap-root .btn.primary:disabled{opacity:.6;cursor:wait}
.aap-root .btn.ghost{background:transparent}
.aap-root .btn.sm{padding:var(--v2-sp-1) 11px;font-size:var(--v2-fs-xs);border-radius:var(--v2-radius-sm)}
.aap-root .btn.rev{background:var(--v2-danger-soft);color:var(--v2-danger);border:1px solid #f3c9c4}
.aap-root .btn.cert{background:var(--v2-success-soft);color:var(--v2-success);border:1px solid #b9e5d3}
.aap-root .hero{background:linear-gradient(120deg,var(--v2-brand-navy),var(--v2-brand-navy-2));color:#fff;border-radius:var(--v2-radius-xl);padding:var(--v2-sp-5) var(--v2-sp-6);margin-bottom:var(--v2-sp-5)}
.aap-root .hero h1{color:#fff;font-size:24px;margin:0 0 var(--v2-sp-2)}
.aap-root .hero p{color:var(--v2-brand-lt);font-size:var(--v2-fs-body);margin:0;max-width:800px;line-height:1.55}
.aap-root .hero .chips{display:flex;gap:var(--v2-sp-2);margin-top:var(--v2-sp-4);flex-wrap:wrap}
.aap-root .hero .chips span{font-size:var(--v2-fs-2xs);background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.16);border-radius:var(--v2-radius-pill);padding:5px var(--v2-sp-3);color:#dfe6f4}
.aap-root .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:18px}
.aap-root .pack{background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-5) var(--v2-sp-5);cursor:pointer;transition:.15s;position:relative}
.aap-root .pack:hover{border-color:var(--v2-brand-steel-2);box-shadow:var(--v2-shadow)}
.aap-root .pack.dim{opacity:.62;cursor:default}.aap-root .pack.dim:hover{box-shadow:none;border-color:var(--v2-border-default)}
.aap-root .pack.prov{border-color:var(--v2-success)}
.aap-root .pack .icn{width:42px;height:42px;border-radius:var(--v2-radius-md);background:linear-gradient(135deg,var(--v2-brand-navy),var(--v2-brand-navy-2));display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;margin-bottom:var(--v2-sp-3)}
.aap-root .pack h3{margin:0 0 5px;font-size:var(--v2-fs-h2)}
.aap-root .pack p{margin:0 0 var(--v2-sp-3);color:var(--v2-text-secondary);font-size:var(--v2-fs-sm);min-height:38px}
.aap-root .pack .meta{display:flex;gap:var(--v2-sp-3);color:var(--v2-text-muted);font-size:var(--v2-fs-2xs);border-top:1px solid var(--v2-border-subtle);padding-top:var(--v2-sp-3);flex-wrap:wrap;align-items:center}
.aap-root .pack .meta b{color:var(--v2-text-secondary);font-weight:700}
.aap-root .pack .meta .cta{margin-left:auto;color:var(--v2-brand-steel);font-weight:700}
.aap-root .dhead{display:flex;gap:18px;align-items:flex-start;background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-5) var(--v2-sp-5);margin-bottom:var(--v2-sp-2)}
.aap-root .dhead .icn{width:56px;height:56px;border-radius:var(--v2-radius-lg);background:linear-gradient(135deg,var(--v2-brand-navy),var(--v2-brand-navy-2));display:flex;align-items:center;justify-content:center;color:#fff;font-size:26px;flex-shrink:0}
.aap-root .dhead .x{flex:1}.aap-root .dhead h1{margin:0 0 var(--v2-sp-1)}
.aap-root .dhead .tags{display:flex;gap:var(--v2-sp-2);margin-top:10px;flex-wrap:wrap}
.aap-root .dhead .act{display:flex;flex-direction:column;gap:var(--v2-sp-2);align-items:flex-end}
.aap-root .installed-chip{font-size:var(--v2-fs-2xs);font-weight:600;color:var(--v2-success);background:var(--v2-success-soft);border-radius:var(--v2-radius-pill);padding:5px var(--v2-sp-3)}
.aap-root .provbanner{display:flex;align-items:center;gap:var(--v2-sp-3);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-3) var(--v2-sp-4);margin-bottom:var(--v2-sp-4);font-size:var(--v2-fs-sm)}
.aap-root .provbanner.preview{background:var(--v2-info-soft);border:1px solid #cfe0ff;color:var(--v2-info)}
.aap-root .provbanner.live{background:var(--v2-success-soft);border:1px solid #b9e5d3;color:var(--v2-success)}
.aap-root .provbanner b{color:var(--v2-text-primary)}.aap-root .provbanner .ic{font-size:16px}
.aap-root .tabs{display:flex;gap:2px;border-bottom:1px solid var(--v2-border-default);margin:var(--v2-sp-3) 0 var(--v2-sp-5);flex-wrap:wrap}
.aap-root .tabs button{border:0;background:transparent;padding:11px 15px;font-size:var(--v2-fs-sm);font-weight:600;color:var(--v2-text-muted);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px}
.aap-root .tabs button:hover{color:var(--v2-text-secondary)}
.aap-root .tabs button.on{color:var(--v2-brand-navy);border-bottom-color:var(--v2-brand-navy)}
.aap-root .tabs button .n{margin-left:var(--v2-sp-1);font-size:10.5px;background:var(--v2-bg-muted);color:var(--v2-text-secondary);border-radius:var(--v2-radius-md);padding:1px 7px;font-weight:700}
.aap-root .sec{background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-5) var(--v2-sp-5);margin-bottom:18px}
.aap-root .sec h2{font-size:var(--v2-fs-sm);text-transform:uppercase;letter-spacing:.5px;color:var(--v2-text-muted);margin:0 0 var(--v2-sp-3);display:flex;align-items:center;gap:10px}
.aap-root .sec h2 .spacer{flex:1}
.aap-root table{width:100%;border-collapse:collapse}
.aap-root th{text-align:left;font-size:var(--v2-fs-2xs);text-transform:uppercase;letter-spacing:.4px;color:var(--v2-text-muted);font-weight:600;padding:var(--v2-sp-2) var(--v2-sp-3);border-bottom:1px solid var(--v2-border-default)}
.aap-root td{padding:11px var(--v2-sp-3);border-bottom:1px solid var(--v2-border-subtle);vertical-align:top;font-size:var(--v2-fs-sm)}
.aap-root tr:last-child td{border-bottom:0}
.aap-root .agname{font-weight:600}.aap-root .agname small{display:block;color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-2xs);margin-top:2px}
.aap-root .lc{font-size:10.5px;font-weight:700;padding:2px var(--v2-sp-2);border-radius:var(--v2-radius-sm);background:var(--v2-success-soft);color:var(--v2-success)}
.aap-root .flow{display:flex;flex-direction:column;align-items:center}
.aap-root .fnode{background:#f7f9fc;border:1px solid #d3ddec;border-radius:var(--v2-radius-md);padding:var(--v2-sp-2) var(--v2-sp-4);font-size:var(--v2-fs-xs);font-weight:600;min-width:320px;text-align:center}
.aap-root .fnode .k{font-size:9.5px;color:var(--v2-brand-steel);letter-spacing:.5px;font-weight:700;display:block}
.aap-root .fterm{background:var(--v2-brand-navy);color:#fff;border:none;border-radius:var(--v2-radius-xl);padding:7px var(--v2-sp-5);font-weight:700;font-size:var(--v2-fs-xs);min-width:auto}
.aap-root .fconn{width:2px;height:16px;background:var(--v2-brand-steel-2)}
.aap-root .clist{border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-md);overflow:hidden}
.aap-root .clist .row{display:flex;align-items:center;gap:var(--v2-sp-3);padding:var(--v2-sp-3) 15px;border-bottom:1px solid var(--v2-border-subtle)}
.aap-root .clist .row:last-child{border-bottom:0}
.aap-root .clist .row .ci{width:32px;height:32px;border-radius:var(--v2-radius-sm);background:var(--v2-bg-muted);display:flex;align-items:center;justify-content:center;font-size:16px}
.aap-root .clist .row .n{font-weight:600;font-size:var(--v2-fs-sm)}.aap-root .clist .row .n small{display:block;color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-2xs)}
.aap-root .clist .row .st{margin-left:auto;display:flex;align-items:center;gap:10px}
.aap-root .cstat{font-size:var(--v2-fs-2xs);font-weight:600;padding:3px var(--v2-sp-2);border-radius:var(--v2-radius-pill)}
.aap-root .cstat.no{background:var(--v2-warning-soft);color:var(--v2-warning)}.aap-root .cstat.yes{background:var(--v2-success-soft);color:var(--v2-success)}
.aap-root .cstat.opt{background:var(--v2-neutral-soft);color:var(--v2-neutral)}
.aap-root .catalog-panel{border:1px dashed var(--v2-brand-steel-2);border-radius:var(--v2-radius-md);padding:var(--v2-sp-4) 18px;margin-top:var(--v2-sp-3);background:#fafcff}
.aap-root .catgroup{margin-bottom:var(--v2-sp-3)}
.aap-root .catgroup .gl{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--v2-text-muted);margin-bottom:7px}
.aap-root .cchips{display:flex;flex-wrap:wrap;gap:var(--v2-sp-2)}
.aap-root .cchip{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--v2-border-default);background:var(--v2-bg-surface);border-radius:var(--v2-radius-md);padding:var(--v2-sp-1) 11px;font-size:var(--v2-fs-xs);cursor:pointer}
.aap-root .cchip:hover{border-color:var(--v2-brand-steel-2)}
.aap-root .cchip .plus{color:var(--v2-brand-steel);font-weight:700}
.aap-root .cchip.custom{border-style:dashed;color:var(--v2-text-secondary)}
.aap-root .gov{display:grid;grid-template-columns:repeat(4,1fr);gap:var(--v2-sp-3);margin-bottom:var(--v2-sp-4)}
.aap-root .gov .g{border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-md);padding:var(--v2-sp-3) var(--v2-sp-4)}
.aap-root .gov .g .k{font-size:var(--v2-fs-2xs);color:var(--v2-text-muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:7px}
.aap-root .gov .g .v{font-size:var(--v2-fs-h2);font-weight:700}.aap-root .gov .g .d{font-size:var(--v2-fs-2xs);color:var(--v2-text-muted);margin-top:var(--v2-sp-1)}
.aap-root .govlist td:first-child{font-weight:600}
.aap-root .ring{display:inline-flex;align-items:center;gap:7px}.aap-root .ring .dot{width:9px;height:9px;border-radius:var(--v2-radius-pill)}
.aap-root .unrated{color:var(--v2-text-muted);font-style:italic}
.aap-root .fw{font-size:10.5px;font-weight:700;padding:2px var(--v2-sp-2);border-radius:var(--v2-radius-sm)}
.aap-root .fw.met{background:var(--v2-success-soft);color:var(--v2-success)}.aap-root .fw.part{background:var(--v2-warning-soft);color:var(--v2-warning)}
.aap-root .chainbar{display:flex;align-items:center;gap:var(--v2-sp-3);margin-bottom:var(--v2-sp-3)}
.aap-root .chainbadge{font-size:var(--v2-fs-2xs);font-weight:700;color:var(--v2-success);background:var(--v2-success-soft);border-radius:var(--v2-radius-pill);padding:5px var(--v2-sp-3)}
.aap-root .aud td{font-size:var(--v2-fs-xs)}
.aap-root .hash{font-size:var(--v2-fs-2xs);color:var(--v2-brand-steel)}
.aap-root .adot{width:11px;height:11px;border-radius:var(--v2-radius-pill);background:var(--v2-brand-steel);display:inline-block;margin-right:var(--v2-sp-2)}
.aap-root .actor{font-weight:600}.aap-root .actor small{color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-2xs);display:block}
.aap-root .empty{border:1px dashed var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-6);text-align:center;background:var(--v2-bg-surface)}
.aap-root .empty .e-ic{font-size:30px;opacity:.5;margin-bottom:var(--v2-sp-3)}
.aap-root .empty h3{margin:0 0 var(--v2-sp-1);font-size:var(--v2-fs-h2)}
.aap-root .empty p{color:var(--v2-text-muted);font-size:var(--v2-fs-sm);max-width:440px;margin:0 auto var(--v2-sp-4)}
.aap-root .scrim{position:fixed;inset:0;background:rgba(10,29,68,.28);display:none;z-index:40}
.aap-root .scrim.on{display:block}
/* RC-131/132 parity: box-shadow paints OUTSIDE the element box, so a closed
   slideout parked off-screen via translateX(100%) bleeds its shadow back onto
   the viewport's right edge — and two always-mounted slideouts stack into a
   visible grey band. Keep the shadow in the .on branch only. bg is a fill (never
   paints outside the box) so it stays unconditional. */
.aap-root .slideout{position:fixed;top:0;right:0;height:100vh;width:var(--v2-slideout-w);max-width:96vw;background:var(--v2-bg-surface);z-index:41;display:flex;flex-direction:column;transform:translateX(100%);transition:transform .22s ease}
.aap-root .slideout.on{transform:translateX(0);box-shadow:var(--v2-shadow-lg)}
.aap-root .sohead{padding:var(--v2-sp-5) var(--v2-sp-5) var(--v2-sp-3);border-bottom:1px solid var(--v2-border-default)}
.aap-root .sohead .cl{position:absolute;right:var(--v2-sp-4);top:var(--v2-sp-4);cursor:pointer;color:var(--v2-text-muted);font-size:18px;background:none;border:none}
.aap-root .sohead h2{margin:0;font-size:18px}
.aap-root .sohead .steps{display:flex;gap:var(--v2-sp-2);margin-top:var(--v2-sp-4)}
.aap-root .sohead .steps .s{flex:1;height:4px;border-radius:3px;background:var(--v2-bg-muted)}.aap-root .sohead .steps .s.on{background:var(--v2-brand-navy)}
.aap-root .sobody{padding:var(--v2-sp-5) var(--v2-sp-5);overflow-y:auto;flex:1}
.aap-root .mstep h3{margin:0 0 var(--v2-sp-1);font-size:15px}.aap-root .mstep .h{color:var(--v2-text-muted);font-size:var(--v2-fs-xs);margin:0 0 var(--v2-sp-4)}
.aap-root .choice{border:2px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-4) 18px;margin-bottom:var(--v2-sp-3);cursor:pointer;display:flex;gap:var(--v2-sp-3);align-items:flex-start}
.aap-root .choice:hover{border-color:var(--v2-brand-steel-2)}
.aap-root .choice.sel{border-color:var(--v2-brand-navy);background:#f8faff}
.aap-root .choice .r{width:18px;height:18px;border-radius:var(--v2-radius-pill);border:2px solid var(--v2-brand-steel-2);flex-shrink:0;margin-top:2px;position:relative}
.aap-root .choice.sel .r{border-color:var(--v2-brand-navy)}.aap-root .choice.sel .r:after{content:'';position:absolute;inset:3px;border-radius:var(--v2-radius-pill);background:var(--v2-brand-navy)}
.aap-root .choice .t b{font-size:var(--v2-fs-body)}.aap-root .choice .t small{display:block;color:var(--v2-text-muted);font-size:var(--v2-fs-xs);margin-top:3px;line-height:1.45}
.aap-root .choice .tag{font-size:10px;font-weight:700;padding:2px var(--v2-sp-2);border-radius:11px;margin-left:auto;flex-shrink:0}
.aap-root .summ{background:var(--v2-bg-muted);border-radius:var(--v2-radius-md);padding:var(--v2-sp-4) 18px;font-size:var(--v2-fs-sm)}
.aap-root .summ .l{display:flex;justify-content:space-between;padding:5px 0;color:var(--v2-text-secondary)}.aap-root .summ .l b{color:var(--v2-text-primary)}
.aap-root .sofoot{display:flex;gap:10px;justify-content:flex-end;padding:var(--v2-sp-4) var(--v2-sp-5);border-top:1px solid var(--v2-border-default)}
.aap-root .done{text-align:center;padding:var(--v2-sp-3) 0}
.aap-root .done .ck{width:56px;height:56px;border-radius:var(--v2-radius-pill);background:var(--v2-success-soft);color:var(--v2-success);font-size:30px;display:flex;align-items:center;justify-content:center;margin:0 auto var(--v2-sp-3)}
.aap-root .fl{display:block;font-size:var(--v2-fs-2xs);font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:var(--v2-text-muted);margin:var(--v2-sp-3) 0 5px}
.aap-root .fin{width:100%;border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-sm);padding:var(--v2-sp-2) var(--v2-sp-3);font-family:inherit;font-size:var(--v2-fs-sm);color:var(--v2-text-primary);background:var(--v2-bg-surface)}
.aap-root .pick{display:flex;flex-direction:column;gap:6px}
.aap-root .pk{display:flex;align-items:center;gap:var(--v2-sp-2);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-md);padding:var(--v2-sp-2) var(--v2-sp-3);font-size:var(--v2-fs-sm);font-weight:600;cursor:pointer}
.aap-root .pk.sel{border-color:var(--v2-brand-navy);background:#f8faff}
.aap-root .pk .mut{color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-xs)}
.aap-root .summary{display:grid;grid-template-columns:repeat(5,1fr);gap:var(--v2-sp-3);margin-bottom:18px}
.aap-root .card{background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);padding:var(--v2-sp-3) var(--v2-sp-4)}
.aap-root .card .k{font-size:10.5px;text-transform:uppercase;letter-spacing:.6px;color:var(--v2-text-muted);margin-bottom:7px}
.aap-root .card .v{font-size:23px;font-weight:700;line-height:1}.aap-root .card .d{font-size:var(--v2-fs-2xs);color:var(--v2-text-muted);margin-top:5px}
.aap-root .bar{height:6px;border-radius:var(--v2-radius-sm);background:var(--v2-bg-muted);margin-top:var(--v2-sp-2);overflow:hidden}.aap-root .bar>span{display:block;height:100%;border-radius:var(--v2-radius-sm)}
.aap-root .tiersplit{display:flex;gap:var(--v2-sp-1);margin-top:var(--v2-sp-2)}.aap-root .tiersplit span{height:8px;border-radius:3px}
.aap-root .filters{display:flex;flex-wrap:wrap;gap:var(--v2-sp-2);align-items:center;margin:var(--v2-sp-1) 0 var(--v2-sp-3)}
.aap-root .seg{display:inline-flex;background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-md);overflow:hidden}
.aap-root .seg button{border:0;background:transparent;padding:var(--v2-sp-1) var(--v2-sp-3);font-size:var(--v2-fs-xs);color:var(--v2-text-secondary);cursor:pointer}
.aap-root .seg button.on{background:var(--v2-brand-navy);color:#fff;font-weight:600}
.aap-root .sel2{background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-md);padding:var(--v2-sp-1) 11px;font-size:var(--v2-fs-xs);color:var(--v2-text-secondary)}
.aap-root .bulkbar{display:flex;align-items:center;gap:var(--v2-sp-3);background:#eef4ff;border:1px solid #cfe0ff;border-radius:var(--v2-radius-md);padding:var(--v2-sp-2) var(--v2-sp-4);margin-bottom:var(--v2-sp-3);font-size:var(--v2-fs-xs);color:var(--v2-info)}
.aap-root .bulkbar b{color:var(--v2-text-primary)}
.aap-root .tablewrap{background:var(--v2-bg-surface);border:1px solid var(--v2-border-default);border-radius:var(--v2-radius-lg);overflow:hidden}
.aap-root .tablewrap th{background:#fbfcfe}
.aap-root .risk{display:flex;align-items:center;gap:var(--v2-sp-2)}
.aap-root .score{font-weight:700;font-size:var(--v2-fs-body);width:24px;text-align:right}
.aap-root .tier{font-size:10px;font-weight:700;text-transform:uppercase;padding:2px var(--v2-sp-1);border-radius:var(--v2-radius-sm)}
.aap-root .tier.critical{background:var(--v2-danger-soft);color:var(--v2-danger)}.aap-root .tier.high{background:var(--v2-warning-alt-soft);color:var(--v2-warning-alt)}
.aap-root .tier.medium{background:var(--v2-info-soft);color:var(--v2-info)}.aap-root .tier.low{background:var(--v2-neutral-soft);color:var(--v2-neutral)}
.aap-root .type{font-weight:600;font-size:var(--v2-fs-xs)}.aap-root .type small{display:block;color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-2xs);margin-top:2px;max-width:210px}
.aap-root .who{font-weight:600;font-size:var(--v2-fs-xs)}.aap-root .who small{display:block;color:var(--v2-text-muted);font-weight:400;font-size:var(--v2-fs-2xs);margin-top:2px}
.aap-root .sys{display:inline-block;font-size:10.5px;font-weight:600;color:var(--v2-text-secondary);background:var(--v2-bg-muted);border-radius:var(--v2-radius-sm);padding:2px 7px}
.aap-root .grant{display:block;color:var(--v2-text-muted);font-size:var(--v2-fs-2xs);margin-top:3px}
.aap-root .dec{font-size:10.5px;font-weight:600;padding:3px var(--v2-sp-2);border-radius:var(--v2-radius-pill);white-space:nowrap}
.aap-root .dec.pending{background:var(--v2-neutral-soft);color:var(--v2-neutral)}.aap-root .dec.certified{background:var(--v2-success-soft);color:var(--v2-success)}
.aap-root .dec.revoked{background:var(--v2-danger-soft);color:var(--v2-danger)}.aap-root .dec.deferred{background:var(--v2-warning-soft);color:var(--v2-warning)}
.aap-root .acts{display:flex;gap:var(--v2-sp-1)}
.aap-root .foot{display:flex;align-items:center;gap:var(--v2-sp-3);padding:11px 15px;color:var(--v2-text-muted);font-size:var(--v2-fs-2xs);border-top:1px solid var(--v2-border-default)}
.aap-root .note{margin-top:var(--v2-sp-4);font-size:var(--v2-fs-xs);color:var(--v2-text-muted);border-left:3px solid var(--v2-brand-steel-2);padding:var(--v2-sp-1) 0 var(--v2-sp-1) var(--v2-sp-3)}
.aap-root .toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--v2-brand-navy);color:#fff;padding:11px var(--v2-sp-5);border-radius:var(--v2-radius-md);font-size:var(--v2-fs-sm);opacity:0;transition:.25s;z-index:60;pointer-events:none}
.aap-root .toast.on{opacity:1}
.aap-root .back{font-size:var(--v2-fs-xs);color:var(--v2-brand-steel);margin-bottom:var(--v2-sp-3);display:inline-block}
`;
